import React from 'react';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from 'src/components/ui/accordion';
import { clsx } from 'clsx';
import { ExpandCollapseIcon } from 'src/pages/OnboardingPage/OnboardingSteps/OnboardingStep2/ExpandCollapseIcon/ExpandCollapseIcon';
import { Controller, useFormContext } from 'react-hook-form';
import { RadioGroup, RadioGroupItem } from 'src/components/ui/radio-group';
import type { ConfigurationFormFieldOption } from 'src/services/configurationService';

const CHANNEL = {
  title: 'Channel',
  icon: (
    <svg xmlns="http://www.w3.org/2000/svg" width="15" height="15" viewBox="0 0 15 15" fill="none">
      <path
        d="M3 0C2.20435 0 1.44129 0.316071 0.87868 0.87868C0.316071 1.44129 0 2.20435 0 3L0 7.5C0 8.29565 0.316071 9.05871 0.87868 9.62132C1.44129 10.1839 2.20435 10.5 3 10.5L7.6275 10.5C7.80442 11.0004 8.15255 11.4222 8.61036 11.6907C9.06817 11.9593 9.60618 12.0573 10.1293 11.9676C10.6524 11.8778 11.127 11.606 11.4691 11.2002C11.8112 10.7944 11.9988 10.2808 11.9988 9.75C11.9988 9.21923 11.8112 8.70556 11.4691 8.29976C11.127 7.89395 10.6524 7.62216 10.1293 7.5324C9.60618 7.44265 9.06817 7.54072 8.61036 7.80928C8.15255 8.07783 7.80442 8.49959 7.6275 9L3 9C2.60218 9 2.22064 8.84196 1.93934 8.56066C1.65804 8.27936 1.5 7.89782 1.5 7.5L1.5 3C1.5 2.60218 1.65804 2.22064 1.93934 1.93934C2.22064 1.65804 2.60218 1.5 3 1.5L7.5 1.5C7.89782 1.5 8.27936 1.65804 8.56066 1.93934C8.84196 2.22064 9 2.60218 9 3H10.5C10.5 2.20435 10.1839 1.44129 9.62132 0.87868C9.05871 0.316071 8.29565 0 7.5 0L3 0ZM10.5 9.75C10.5 9.94891 10.421 10.1397 10.2803 10.2803C10.1397 10.421 9.94891 10.5 9.75 10.5C9.55109 10.5 9.36032 10.421 9.21967 10.2803C9.07902 10.1397 9 9.94891 9 9.75C9 9.55109 9.07902 9.36032 9.21967 9.21967C9.36032 9.07902 9.55109 9 9.75 9C9.94891 9 10.1397 9.07902 10.2803 9.21967C10.421 9.36032 10.5 9.55109 10.5 9.75ZM12 15C12.7956 15 13.5587 14.6839 14.1213 14.1213C14.6839 13.5587 15 12.7956 15 12L15 7.5C15 6.70435 14.6839 5.94129 14.1213 5.37868C13.5587 4.81607 12.7956 4.5 12 4.5L7.3725 4.5C7.19558 3.99959 6.84745 3.57783 6.38964 3.30928C5.93183 3.04072 5.39382 2.94265 4.8707 3.0324C4.34758 3.12216 3.87303 3.39395 3.53092 3.79976C3.18881 4.20556 3.00118 4.71923 3.00118 5.25C3.00118 5.78077 3.18881 6.29444 3.53092 6.70024C3.87303 7.10605 4.34758 7.37784 4.8707 7.4676C5.39382 7.55735 5.93183 7.45928 6.38964 7.19072C6.84745 6.92217 7.19558 6.50041 7.3725 6L12 6C12.3978 6 12.7794 6.15804 13.0607 6.43934C13.342 6.72064 13.5 7.10218 13.5 7.5L13.5 12C13.5 12.3978 13.342 12.7794 13.0607 13.0607C12.7794 13.342 12.3978 13.5 12 13.5H7.5C7.10218 13.5 6.72064 13.342 6.43934 13.0607C6.15804 12.7794 6 12.3978 6 12H4.5C4.5 12.7956 4.81607 13.5587 5.37868 14.1213C5.94129 14.6839 6.70435 15 7.5 15H12ZM5.25 6C5.05109 6 4.86032 5.92098 4.71967 5.78033C4.57902 5.63968 4.5 5.44891 4.5 5.25C4.5 5.05109 4.57902 4.86032 4.71967 4.71967C4.86032 4.57902 5.05109 4.5 5.25 4.5C5.44891 4.5 5.63968 4.57902 5.78033 4.71967C5.92098 4.86032 6 5.05109 6 5.25C6 5.44891 5.92098 5.63968 5.78033 5.78033C5.63968 5.92098 5.44891 6 5.25 6Z"
        fill="#05050D"
      />
    </svg>
  ),
  description:
    'Select where your AI will communicate — quick chat replies or structured email responses.',
};
// Keyed by the field's option keys.
const CHANNEL_OPTIONS: Record<
  string,
  { id: number; title: string; description?: React.ReactNode }
> = {
  chat: {
    id: 0,
    title: 'Chat 💬',
    description: (
      <>
        Responses optimized for quick reading.
        <br />
      </>
    ),
  },
  email: {
    id: 1,
    title: 'Email ✉️',
    description: 'Well-structured responses optimized for email communication.',
  },
};

export const ChannelAccordion = ({
  accordionValue,
  setAccordionValue,
  disabled = false,
  options = [],
}: {
  // The open accordion item; the three step-5 accordions share it so one is open at a time.
  accordionValue: string;
  setAccordionValue: (value: string) => void;
  disabled?: boolean;
  options?: ConfigurationFormFieldOption[];
}) => {
  const { control, watch } = useFormContext();
  const channel = watch('pf_tone_of_voice_channel');

  return (
    <div className={'flex flex-col gap-3'}>
      <div className={'flex flex-col gap-1'}>
        <div className={'flex flex-row gap-2.5 items-center'}>
          {CHANNEL.icon}
          <p className="text-sm font-semibold text-ebony">{CHANNEL.title}</p>
        </div>
        <p className={'text-sm text-ebony_opacity_50 font-medium'}>{CHANNEL.description}</p>
      </div>
      <div>
        {channel && (
          <Accordion
            value={accordionValue}
            onValueChange={setAccordionValue}
            type="single"
            className="w-full min-w-[455px] max-w-[455px]"
            collapsible={true}
            disabled={disabled}
          >
            <AccordionItem
              value="channel-card"
              className={clsx(
                'group border border-[#B8C1C633] rounded-sm-5 bg-white transition-shadow shadow-none',
                !disabled && 'hover:shadow-[0_2px_2px_rgba(0,0,0,0.25)]',
              )}
            >
              <AccordionTrigger hideDefaultIcon className="py-2.5 px-5">
                <div className="flex items-center justify-between w-full">
                  <div className={'flex flex-row gap-[7px]'}>
                    <div className={'text-start'}>
                      <p className={'text-[#7A6BE5] text-sm font-semibold'}>
                        {CHANNEL_OPTIONS[channel].title}
                      </p>
                      <p className={'text-ebony_opacity_30 text-sm font-medium'}>
                        {CHANNEL_OPTIONS[channel].description}
                      </p>
                    </div>
                  </div>
                  {!disabled && (
                    <ExpandCollapseIcon
                      className={clsx(
                        'w-[12px] opacity-50 rotate-0 transition group-hover:opacity-100',
                        accordionValue === 'channel-card' && 'rotate-180',
                      )}
                    />
                  )}
                </div>
              </AccordionTrigger>
              <AccordionContent className="px-5 pb-0 bg-light">
                <div className={'flex flex-col gap-2.5'}>
                  <Controller
                    control={control}
                    name="pf_tone_of_voice_channel"
                    render={({ field: { onChange, value }, fieldState: { error } }) => (
                      <RadioGroup
                        onValueChange={onChange}
                        value={value}
                        className="flex flex-col mt-5 mb-2.5 gap-5"
                      >
                        {options?.map((option) => {
                          const item = {
                            ...CHANNEL_OPTIONS[option.key],
                            ...option,
                          };

                          return (
                            <div
                              key={`${item.id}-${item.key}`}
                              className={'flex items-start gap-2.5'}
                            >
                              <RadioGroupItem
                                className={'mt-1'}
                                value={item.value}
                                id={item.value}
                              />
                              <label htmlFor={item.value} className="cursor-pointer">
                                <p className="text-sm font-semibold text-ebony opacity-80 hover:opacity-100 transition-colors">
                                  {item.title || item.key}
                                </p>
                                {item.description && (
                                  <p className={'text-sm text-ebony_opacity_50 font-medium'}>
                                    {item.description}
                                  </p>
                                )}
                              </label>
                            </div>
                          );
                        })}
                      </RadioGroup>
                    )}
                  />
                </div>
              </AccordionContent>
            </AccordionItem>
          </Accordion>
        )}
      </div>
    </div>
  );
};
